import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import moment from 'moment/moment';
import LeadsActions from '../../Redux/LeadsRedux';
import { checkHostConstraint, HOSTS_NAMES } from '../../Routing/HostConstraint';
import {
  Box,
  Card,
  CardContent,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  LinearProgress,
  MenuItem,
  OutlinedInput,
  Paper,
  Select,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Typography,
  List,
} from '@material-ui/core';
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import Collapse from '@material-ui/core/Collapse';
import ClearIcon from '@material-ui/icons/Clear';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import OrganisationMembershipActions from '../../Redux/OrganisationMembershipRedux';
import Roles from '../../Acl/Roles';
import { TableFooter } from '@material-ui/core/';
import TablePagination from '@material-ui/core/TablePagination';
import ListItem from '@mui/material/ListItem';

type Props = {
  organisation: Object,
};

const formatNumber = number => {
  return new Intl.NumberFormat('fr-FR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(number || 0);
};

const formatInteger = number => {
  return new Intl.NumberFormat('fr-FR').format(number || 0);
};

const useStyles = makeStyles(theme => ({
  title: {
    fontWeight: '800',
    textTransform: 'none',
  },
  grow: {
    flexGrow: 1,
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
  },
  appbar: {
    display: 'flex',
    flexDirection: 'row',
    marginBottom: theme.spacing(1),
    backgroundColor: 'white',
    flexGrow: 1,
  },
  progress: {
    width: '100%',
  },
  button: {
    marginLeft: theme.spacing(1),
  },
  btnColor: {
    backgroundColor: theme.current.primary.main,
    color: 'white',
    '&:hover': {
      backgroundColor: theme.current.primary.dark,
    },
  },
  tabs: {
    borderBottom: `1px solid ${theme.palette.divider}`,
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  statsCard: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  },
  statsValue: {
    fontSize: '2rem',
    fontWeight: 600,
    color: theme.palette.primary.main,
  },
  statsLabel: {
    fontSize: '0.875rem',
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1),
  },
  chartContainer: {
    height: 300,
    width: '100%',
  },
  tableContainer: {
    marginTop: theme.spacing(2),
  },
  noData: {
    textAlign: 'center',
    color: theme.palette.text.secondary,
    padding: theme.spacing(3),
  },
}));

type RowProps = {
  lead: Object,
};

function Row({ lead }: RowProps) {
  const [open, setOpen] = React.useState(false);
  const { t } = useTranslation();

  const getLeadTotalDistance = lead => {
    return parseFloat(lead.meta?.volunteer_departure_distance || 0).toFixed(2);
  };

  const getLeadTotalAmount = lead => {
    return parseFloat(lead.meta?.partner_volunteer_refund_amount || 0).toFixed(2);
  };

  return (
    <React.Fragment>
      <TableRow>
        <TableCell>
          <IconButton aria-label="expand row" size="small" onClick={() => setOpen(!open)}>
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell>{moment(lead.meta.departure_date).format('LLL')}</TableCell>
        <TableCell>{lead?.responsible?.fullname}</TableCell>
        <TableCell>
          Pour : {lead.helped ? lead.helped.fullname : lead.helper?.fullname}
          <br />
          {lead.meta.departureFormattedAddress}
        </TableCell>
        <TableCell>{getLeadTotalDistance(lead)} km</TableCell>
        <TableCell>{getLeadTotalAmount(lead)} €</TableCell>
        <TableCell></TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={5}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box p={5}>
              <Typography variant="h6" gutterBottom component="div">
                Détail :
              </Typography>
              <List>
                <ListItem>
                  {t('APP.FAMILY.TRANSPORT.HEADER.ROUNDTRIP')} :{' '}
                  {lead.meta.round_trip ? t('YES') : t('NO')}
                </ListItem>
                <ListItem>
                  {t('APP.FAMILY.TRANSPORT.HEADER.HELPED_DISTANCE')} :{' '}
                  {parseFloat(lead.meta?.distance || 0)} km
                </ListItem>
                <ListItem>
                  {t('APP.FAMILY.TRANSPORT.HEADER.VOLUNTEER_DISTANCE')} :{' '}
                  {lead.meta?.volunteer_departure_distance
                    ? parseFloat(lead.meta?.volunteer_departure_distance).toFixed(2) + ' km'
                    : t('APP.FAMILY.TRANSPORT.VOLUNTEER_DISTANCE_NOT_CALCULATED')}
                </ListItem>
                <ListItem>
                  {t('APP.FAMILY.TRANSPORT.HEADER.AMOUNT')} :{' '}
                  {parseFloat(lead.meta?.estimatedPrice || 0).toFixed(2) + '€'}
                </ListItem>
              </List>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </React.Fragment>
  );
}

type StatCardProps = {
  label: string,
  value: string | number,
  unit?: string,
  color?: string,
};

const StatCard = ({ label, value, unit = '', color = 'primary' }: StatCardProps) => {
  const classes = useStyles();
  return (
    <Card className={classes.statsCard}>
      <CardContent>
        <Typography className={classes.statsLabel} gutterBottom>
          {label}
        </Typography>
        <Typography className={classes.statsValue} style={{ color: color }}>
          {value} {unit}
        </Typography>
      </CardContent>
    </Card>
  );
};

type TabPanelProps = {
  children: React.ReactNode,
  value: number,
  index: number,
};

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`statistics-tabpanel-${index}`}
      aria-labelledby={`statistics-tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
}
const Statistics = ({ organisation }: Props) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const leads = useSelector(state => state.leads.leads);

  const stats = useSelector(state => state.leads.stats);
  const totalLeads = useSelector(state => state.leads.totalleads);
  const isFetching = useSelector(state => state.leads.isFetching.getOrganisationLeads);
  const members = useSelector(state => state.organisationMembership.members);

  const currentYear = new Date().getFullYear();
  const months = moment.months();
  const years = [];
  const [tabValue, setTabValue] = React.useState(0);
  const [pageFilters, setPageFilters] = React.useState({
    currentPage: 0,
    filterStatus: 'notArchived',
    filterTransport: true,
    year: currentYear,
    month: moment().month() + 1,
    sortBy: checkHostConstraint(HOSTS_NAMES.mormal) ? 'departure_date' : 'created_at',
    sortDirection: 'desc',
    responsible: null,
  });

  for (let i = currentYear - 5; i <= currentYear + 5; i++) {
    years.push(i);
  }

  const volunteers =
    members &&
    members?.length !== 0 &&
    members?.every(m => m?.role === Roles.VOLUNTEER) &&
    members?.every(m => m?.organisation_slug === organisation?.slug);

  const leadsFetch =
    !isFetching &&
    leads &&
    leads.length !== 0 &&
    leads?.every(l => l?.organisation?.slug === organisation?.slug);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  React.useEffect(() => {
    dispatch(
      LeadsActions.getOrganisationLeadsRequest(
        organisation.holding_slug,
        organisation.slug,
        pageFilters.filterStatus === 'archived' && pageFilters.filterStatus,
        pageFilters.currentPage,
        pageFilters.sortBy,
        pageFilters.sortDirection,
        pageFilters.filterTransport,
        pageFilters.year,
        pageFilters.month,
        pageFilters.responsible,
      ),
    );
  }, [dispatch, pageFilters, organisation]);

  React.useEffect(() => {
    dispatch(
      OrganisationMembershipActions.getOrganisationMembershipRequest(
        organisation.holding_slug,
        organisation.slug,
        Roles.VOLUNTEER,
      ),
    );
  }, [organisation, dispatch]);

  const globalStats = stats?.global || {};
  const beneficiaryStats = stats?.beneficiary || {};
  const volunteerStats = stats?.volunteer || {};
  const monthlyData = stats?.monthly || [];
  const travelReasons = stats?.travelReasons || [];
  const archivingMotives = stats?.archivingMotives || [];

  const formattedMonthlyData = monthlyData.map(item => ({
    month: item.month || item.label,
    demandes: item.requests || item.demandes || 0,
    trajets: item.trips || item.trajets || 0,
  }));

  return (
    <Grid container>
      {/* Filtres */}
      <Grid item xs={12}>
        <Paper>
          <Box p={2}>
            <Grid container justifyContent={'flex-start'} spacing={2}>
              <Grid item xs={12} sm={4}>
                <FormControl variant="outlined" component="fieldset" fullWidth>
                  <InputLabel id="year">Année</InputLabel>
                  <Select
                    id={'year'}
                    value={pageFilters.year}
                    onChange={event =>
                      setPageFilters({
                        ...pageFilters,
                        year: event.target.value,
                      })
                    }
                    input={<OutlinedInput labelWidth={50} name="year" />}
                    fullWidth
                    variant="outlined"
                  >
                    {years.map(year => (
                      <MenuItem key={year} value={year}>
                        {year}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl variant="outlined" component="fieldset" fullWidth>
                  <InputLabel id="month">Mois</InputLabel>
                  <Select
                    id={'month'}
                    endAdornment={
                      pageFilters.month && (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() =>
                              setPageFilters({
                                ...pageFilters,
                                month: null,
                              })
                            }
                          >
                            <ClearIcon />
                          </IconButton>
                        </InputAdornment>
                      )
                    }
                    value={pageFilters.month || ''}
                    onChange={event =>
                      setPageFilters({
                        ...pageFilters,
                        month: event.target.value || null,
                      })
                    }
                    input={<OutlinedInput labelWidth={50} name="month" />}
                    fullWidth
                    variant="outlined"
                  >
                    <MenuItem value="">Tous les mois</MenuItem>
                    {months.map((month, index) => (
                      <MenuItem key={index} value={index + 1}>
                        {month}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl variant="outlined" component="fieldset" fullWidth>
                  <InputLabel id="volunteer">Bénévole</InputLabel>
                  <Select
                    id={'volunteer'}
                    endAdornment={
                      pageFilters.responsible && (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() =>
                              setPageFilters({
                                ...pageFilters,
                                responsible: null,
                              })
                            }
                          >
                            <ClearIcon />
                          </IconButton>
                        </InputAdornment>
                      )
                    }
                    value={pageFilters.responsible || ''}
                    onChange={event =>
                      setPageFilters({
                        ...pageFilters,
                        responsible: event.target.value || null,
                      })
                    }
                    input={<OutlinedInput labelWidth={80} name="volunteer" />}
                    fullWidth
                    variant="outlined"
                  >
                    <MenuItem value="">Tous les bénévoles</MenuItem>
                    {volunteers &&
                      members
                        .sort((volunteerA, volunteerB) =>
                          volunteerA.helper.fullname.localeCompare(
                            volunteerB.helper.fullname,
                            'fr',
                            { sensitivity: 'base' },
                          ),
                        )
                        .map(volunteer => (
                          <MenuItem key={volunteer.helper.id} value={volunteer.helper.id}>
                            {volunteer.helper.fullname}
                          </MenuItem>
                        ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Box>
        </Paper>
      </Grid>

      <Grid item xs={12}>
        <Paper className={classes.tabs}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            indicatorColor="primary"
            textColor="primary"
            aria-label="statistics tabs"
          >
            <Tab label="Tableau de bord global" />
            <Tab label="Informations bénéficiaires" />
            <Tab label="Informations bénévoles" />
            <Tab label="Liste des transports" />
          </Tabs>
        </Paper>
      </Grid>

      <Grid item xs={12}>
        {isFetching && <LinearProgress className={classes.progress} />}

        <TabPanel value={tabValue} index={0}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <StatCard
                label="Nombre de demandes enregistrées"
                value={formatInteger(globalStats.totalRequests || 0)}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <StatCard
                label="Nombre de trajets effectués"
                value={formatInteger(globalStats.completedTrips || 0)}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <StatCard
                label="Nombre de transports archivés"
                value={formatInteger(globalStats.archivedTransports || 0)}
              />
            </Grid>

            <Grid item xs={12} lg={6}>
              <Paper>
                <Box p={2}>
                  <Typography variant="h6" gutterBottom>
                    Demandes par mois
                  </Typography>
                  {formattedMonthlyData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={formattedMonthlyData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="month" />
                        <YAxis />
                        <Tooltip />
                        <Bar dataKey="demandes" fill="#3f51b5" name="Demandes" />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <Typography className={classes.noData}>
                      Aucune donnée disponible pour cette période
                    </Typography>
                  )}
                </Box>
              </Paper>
            </Grid>

            <Grid item xs={12} lg={6}>
              <Paper>
                <Box p={2}>
                  <Typography variant="h6" gutterBottom>
                    Trajets effectués par mois
                  </Typography>
                  {formattedMonthlyData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={formattedMonthlyData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="month" />
                        <YAxis />
                        <Tooltip />
                        <Bar dataKey="trajets" fill="#4caf50" name="Trajets" />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <Typography className={classes.noData}>
                      Aucune donnée disponible pour cette période
                    </Typography>
                  )}
                </Box>
              </Paper>
            </Grid>

            <Grid item xs={12}>
              <Paper>
                <Box p={2}>
                  <Typography variant="h6" gutterBottom>
                    Motifs de déplacement des bénéficiaires
                  </Typography>
                  {travelReasons.length > 0 ? (
                    <TableContainer>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell>Motif</TableCell>
                            <TableCell align="right">Nombre</TableCell>
                            <TableCell align="right">Pourcentage</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {travelReasons.map((reason, index) => (
                            <TableRow key={index}>
                              <TableCell>{reason.motif || reason.label}</TableCell>
                              <TableCell align="right">{reason.count || 0}</TableCell>
                              <TableCell align="right">
                                {formatNumber(reason.percentage || 0)}%
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Typography className={classes.noData}>
                      Aucun motif de déplacement enregistré pour cette période
                    </Typography>
                  )}
                </Box>
              </Paper>
            </Grid>

            <Grid item xs={12}>
              <Paper>
                <Box p={2}>
                  <Typography variant="h6" gutterBottom>
                    Motifs d&apos;archivage
                  </Typography>
                  {archivingMotives.length > 0 ? (
                    <TableContainer>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell>Motif</TableCell>
                            <TableCell align="right">Nombre</TableCell>
                            <TableCell align="right">Pourcentage</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {archivingMotives.map((motive, index) => (
                            <TableRow key={index}>
                              <TableCell>{motive.motif || motive.label}</TableCell>
                              <TableCell align="right">{motive.count || 0}</TableCell>
                              <TableCell align="right">
                                {formatNumber(motive.percentage || 0)}%
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Typography className={classes.noData}>
                      Aucun archivage enregistré pour cette période
                    </Typography>
                  )}
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <StatCard
                label="Distance totale parcourue pour les trajets bénéficiaires"
                value={formatNumber(beneficiaryStats.totalDistance || 0)}
                unit="km"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <StatCard
                label="Distance moyenne parcourue pour les trajets bénéficiaires"
                value={formatNumber(beneficiaryStats.averageDistance || 0)}
                unit="km"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <StatCard
                label="Coût total des trajets enregistrés"
                value={formatNumber(beneficiaryStats.totalCost || 0)}
                unit="€"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <StatCard
                label="Nombre de bénéficiaires distincts sur la période"
                value={formatInteger(beneficiaryStats.uniqueBeneficiaries || 0)}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <StatCard
                label="Moyenne d'âge des bénéficiaires"
                value={Math.round(beneficiaryStats.averageAge || 0)}
                unit="ans"
              />
            </Grid>

            <Grid item xs={12}>
              <Paper>
                <Box p={2}>
                  <Typography variant="h6" gutterBottom>
                    Nombre de bénéficiaires par ville
                  </Typography>
                  {beneficiaryStats.byCity && beneficiaryStats.byCity.length > 0 ? (
                    <TableContainer>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell>Ville</TableCell>
                            <TableCell align="right">Nombre de bénéficiaires</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {beneficiaryStats.byCity.map((city, index) => (
                            <TableRow key={index}>
                              <TableCell>{city.name || city.ville}</TableCell>
                              <TableCell align="right">{city.count || city.nombre || 0}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Typography className={classes.noData}>
                      Aucune donnée disponible pour cette période
                    </Typography>
                  )}
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <StatCard
                label="Distance totale parcourue pour les trajets Haut-le-pied"
                value={formatNumber(volunteerStats.totalDistance || 0)}
                unit="km"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <StatCard
                label="Distance moyenne pour les trajets Haut-le-pied"
                value={formatNumber(volunteerStats.averageDistance || 0)}
                unit="km"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <StatCard
                label="Remboursement total Haut-le-pied"
                value={formatNumber(volunteerStats.totalRefund || 0)}
                unit="€"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <StatCard
                label="Remboursement moyen Haut-le-pied"
                value={formatNumber(volunteerStats.averageRefund || 0)}
                unit="€"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <StatCard
                label="Moyenne d'âge bénévoles"
                value={Math.round(volunteerStats.averageAge || 0)}
                unit="ans"
              />
            </Grid>

            <Grid item xs={12}>
              <Paper>
                <Box p={2}>
                  <Typography variant="h6" gutterBottom>
                    Nombre de bénévoles par ville
                  </Typography>
                  {volunteerStats.byCity && volunteerStats.byCity.length > 0 ? (
                    <TableContainer>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell>Ville</TableCell>
                            <TableCell align="right">Nombre de bénévoles</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {volunteerStats.byCity.map((city, index) => (
                            <TableRow key={index}>
                              <TableCell>{city.name || city.ville}</TableCell>
                              <TableCell align="right">{city.count || city.nombre || 0}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Typography className={classes.noData}>
                      Aucune donnée disponible pour cette période
                    </Typography>
                  )}
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </TabPanel>
        <TabPanel value={tabValue} index={3}>
          <Grid container spacing={3}>
            <Grid item xs={true}>
              <div className={classes.transport}>
                {leadsFetch && (
                  <TableContainer component={Paper}>
                    <Table aria-label="collapsible table" width={'100%'}>
                      <TableHead>
                        <TableRow>
                          <TableCell />
                          <TableCell>{t('APP.FAMILY.TRANSPORT.HEADER.DATE')}</TableCell>
                          <TableCell>{t('APP.FAMILY.TRANSPORT.HEADER.VOLUNTEER')}</TableCell>
                          <TableCell>{t('APP.FAMILY.TRANSPORT.HEADER.DESCRIPTION')}</TableCell>
                          <TableCell>
                            {t('APP.FAMILY.TRANSPORT.HEADER.VOLUNTEER_DISTANCE')}
                          </TableCell>
                          <TableCell>{t('APP.FAMILY.TRANSPORT.HEADER.VOLUNTEER_AMOUNT')}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {leads.map((lead, index) => (
                          <Row key={index} lead={lead} />
                        ))}
                      </TableBody>
                      <TableFooter>
                        <TableRow>
                          <TablePagination
                            rowsPerPageOptions={false}
                            colSpan={6}
                            count={totalLeads}
                            rowsPerPage={30}
                            page={pageFilters.currentPage}
                            onPageChange={(event, page) =>
                              setPageFilters({ ...pageFilters, currentPage: page })
                            }
                            labelDisplayedRows={({ from, to, count }) =>
                              `${from}-${to} sur ${count !== -1 ? count : `plus de ${to}`}`
                            }
                          />
                        </TableRow>
                      </TableFooter>
                    </Table>
                  </TableContainer>
                )}
              </div>
            </Grid>
          </Grid>
        </TabPanel>
      </Grid>
    </Grid>
  );
};

export default Statistics;
